import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "antd";
import {
  HiOutlineArrowTrendingDown,
  HiOutlineArrowTrendingUp,
  HiOutlineArrowsRightLeft,
  HiOutlineCreditCard,
  HiOutlineExclamationTriangle,
  HiOutlineUsers,
  HiOutlineXMark,
  HiOutlineCheck,
  HiOutlineSparkles,
  HiOutlineBolt,
  HiOutlineSquares2X2,
} from "react-icons/hi2";
import { billingApi, UpcomingInvoice, usersApi } from "../../api";
import { APP_ROUTES } from "../../data-access";
import { storeAuthActions } from "../../store/slices/auth";
import { IPlan, IResponseError, IUser, PricingPlan } from "../../data-access";
import { RootState } from "../../store";
import { storeBillingActions } from "../../store/slices/billing";
import { BackButton, FormButton } from "../../ui";
import { handleFailedRequest, notify, updateExtensionCookies } from "../../util";
import { ByokKeyAlert } from "../byok-key";
import { Plan, PlanSkeleton } from "./plan";
import "./plans.scss";
import { syncAuthWithExtension } from "../../util/auth-sync";
import { useUser } from "../../hooks";

export type BillingInterval = "monthly" | "yearly";

interface ChangePreview {
  amount_due: number;
  credit: number;
  subtotal: number;
  tax: number;
  new_charge: number;
  proration_credit: number;
  current_plan_name: string | null;
  currency: string;
  new_plan: string;
  new_price: number;
  interval: string;
  seats: number;
  is_flex_migration?: boolean;
  is_downgrade?: boolean;
  credits_carry_over?: number;
}


// Shown beside the plan card when only one plan is on sale (Flex): the card
// keeps a short pitch, this panel carries the full grouped feature list.
const PLAN_FEATURE_GROUPS = [
  {
    title: "AI Tools",
    icon: HiOutlineSparkles,
    items: [
      "Check Fit Job Analyzer (0-100 score)",
      "AI Cover Letter + Screening Q&A",
      "Cover Letter Templates with per-template reply rate",
      "AI Profile Highlights (best work per bid)",
      "Upwork Profile Score: 29-point audit",
      "Adaptive AI engine (auto-failover)",
    ],
  },
  {
    title: "Bidding",
    icon: HiOutlineBolt,
    items: [
      "Quick Apply from any Upwork job",
      "Proposal Autofill (review then submit)",
      "Auto-Bidding scanner (24/7, hands-free)",
      "Custom Match Algorithm prompt",
      "Custom Red Flags prompt",
      "15+ Auto-Bidding settings (caps, schedule, scan)",
    ],
  },
  {
    title: "Workspace",
    icon: HiOutlineSquares2X2,
    items: [
      "Full analytics dashboard + Activity Heatmap",
      "Multi-profile + Weekly recap emails",
      "Pipedrive CRM sync",
      "Priority extension features as they ship",
      "Extra credit packs anytime",
    ],
  },
];

export const Plans = (): JSX.Element => {
  const { auth, currentUser } = useUser();
  const hasActiveSubscription = currentUser?.has_active_subscription;
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("monthly");
  const [planIdInProgressToBeCurrent, setPlanIdInProgressToBeCurrent] =
    useState<number | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [showSeatsModal, setShowSeatsModal] = useState(false);

  // Upcoming invoice (for pending downgrade detection)
  const [upcomingInvoice, setUpcomingInvoice] = useState<UpcomingInvoice | null>(null);

  // Plan change state
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [changePreview, setChangePreview] = useState<ChangePreview | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<BillingInterval>("monthly");
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  const allPlans = useSelector((state: RootState) => state.billing.plans);
  // Flex is the only plan on sale. Lite/Pro/Max (incl. archived old-price
  // rows) stay visible only to a team paying for one right now, so they can
  // keep or cancel it. Trials see Flex only (temporary: the backend still
  // starts trials on Max - see getDisplayPlanName). An expired trial gets
  // parked on Lite by the backend, but never chose it, so it sees Flex only.
  const isOnLegacyPlanNow =
    !!hasActiveSubscription || currentUser?.billing_status === "past_due";
  const flexPlans = allPlans.filter((plan) => plan.name === PricingPlan.Flex && !plan.is_archived);
  const userLegacyPlan = isOnLegacyPlanNow
    ? allPlans.find((plan) => plan.is_selected && plan.name !== PricingPlan.Flex) || null
    : null;
  const plans = userLegacyPlan ? [...flexPlans, userLegacyPlan] : flexPlans;
  const currentPlan = useSelector(
    (state: RootState) => state.billing.currentPlan
  );
  // A paying legacy (Lite/Pro/Max) team looking at Flex: always an immediate
  // upgrade, never a downgrade, whatever the price difference.
  const isFlexMigrationTarget = (plan: IPlan): boolean =>
    !!hasActiveSubscription &&
    plan.name === PricingPlan.Flex &&
    !!currentPlan &&
    currentPlan.name !== PricingPlan.Flex;
  const teamMembers = useSelector((state: RootState) => state.team.members);
  const totalMembers = teamMembers?.length || currentUser?.teammates || 1;

  const dispatch = useDispatch();

  const refreshPlansAndUser = (): void => {
    billingApi
      .getPlans()
      .then((response: { data: IPlan[] }) => {
        dispatch(
          storeBillingActions.update({
            plans: response.data.filter((plan) => !plan.is_trial),
            currentPlan: response.data.find(
              (plan: IPlan) => plan.is_selected && !plan.is_trial
            ),
          })
        );
      })
      .catch(() => {});

    usersApi.getMe().then((response: { data: IUser }) => {
      const userData = { user: response.data };
      dispatch(storeAuthActions.setUserData(userData));
      syncAuthWithExtension("AUTH_UPDATE", {
        user: response.data,
        accessToken: auth?.access_token,
        refreshToken: auth?.refresh_token,
      });
      updateExtensionCookies(userData);
    });

    billingApi.getUpcomingInvoice()
      .then(setUpcomingInvoice)
      .catch(() => {});
  };

  useEffect(() => {
    setIsProcessing(true);
    billingApi
      .getPlans()
      .then((response: { data: IPlan[] }) => {
        dispatch(
          storeBillingActions.update({
            plans: response.data.filter((plan) => !plan.is_trial),
            currentPlan: response.data.find(
              (plan: IPlan) => plan.is_selected && !plan.is_trial
            ),
          })
        );
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsProcessing(false));
  }, []);

  useEffect(() => {
    if (hasActiveSubscription) {
      billingApi.getUpcomingInvoice()
        .then(setUpcomingInvoice)
        .catch(() => {});
    }
  }, [hasActiveSubscription]);

  const pendingPlanName = upcomingInvoice?.pending_plan?.name || null;
  const isCancelingSubscription = upcomingInvoice?.canceling === true;
  const cancelAtDate = upcomingInvoice?.cancel_at || null;

  const currentPlanAsJSON = JSON.stringify(currentPlan);
  useEffect(() => {
    usersApi.getMe().then((response: { data: IUser }) => {
      const userData = { user: response.data };
      dispatch(storeAuthActions.setUserData(userData));
      syncAuthWithExtension("AUTH_UPDATE", {
        user: response.data,
        accessToken: auth?.access_token,
        refreshToken: auth?.refresh_token,
      });
      updateExtensionCookies(userData);
    });
  }, [currentPlanAsJSON]);

  const handleCancelSubscription = (): void => {
    setIsCanceling(true);
    billingApi
      .cancelSubscription()
      .then((response) => {
        const cancelDate = response.cancel_at
          ? new Date(response.cancel_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
          : null;
        notify("success", `Subscription will be canceled${cancelDate ? ` on ${cancelDate}` : " at the end of the billing period"}.`);
        setShowCancelModal(false);
        refreshPlansAndUser();
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsCanceling(false));
  };

  const handleSelect = (id: number, interval: BillingInterval = "monthly"): void => {
    if (hasActiveSubscription) {
      // Active subscription → show change preview modal
      setSelectedPlanId(id);
      setSelectedInterval(interval);
      setIsLoadingPreview(true);
      setShowChangeModal(true);
      setPlanIdInProgressToBeCurrent(id);

      billingApi
        .previewPlanChange(id, interval)
        .then((preview) => {
          setChangePreview(preview);
        })
        .catch((error: IResponseError) => {
          handleFailedRequest(error.status, error.text, error.details);
          setShowChangeModal(false);
        })
        .finally(() => {
          setIsLoadingPreview(false);
          setPlanIdInProgressToBeCurrent(null);
        });
    } else {
      // No subscription → Checkout (with seats confirmation if team > 1)
      if (totalMembers > 1) {
        setSelectedPlanId(id);
        setSelectedInterval(interval);
        setShowSeatsModal(true);
      } else {
        proceedToCheckout(id, interval);
      }
    }
  };

  const proceedToCheckout = (id: number, interval: BillingInterval): void => {
    // GA4 funnel: user is leaving the app to enter Stripe Checkout. Fire
    // `begin_checkout` so the funnel step before `purchase` has a clear
    // counterpart in GA4 Explore.
    const selectedPlanData = plans.find((p) => p.id === id);
    if ((window as any).gtag && selectedPlanData) {
      const price = interval === "yearly" && selectedPlanData.yearly_price
        ? +selectedPlanData.yearly_price
        : +selectedPlanData.price;
      (window as any).gtag("event", "begin_checkout", {
        currency: "USD",
        value: price,
        items: [{
          item_id: String(selectedPlanData.id),
          item_name: selectedPlanData.name,
          price,
          quantity: 1,
          item_category: interval,
        }],
      });
    }

    setIsProcessing(true);
    setPlanIdInProgressToBeCurrent(id);
    billingApi
      .subscribe(id, interval)
      .then((response) => {
        window.location.href = response.checkout_url;
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => {
        setIsProcessing(false);
        setPlanIdInProgressToBeCurrent(null);
      });
  };

  const handleConfirmSeats = (): void => {
    if (!selectedPlanId) return;
    setShowSeatsModal(false);
    proceedToCheckout(selectedPlanId, selectedInterval);
  };

  const handleConfirmChange = (): void => {
    if (!selectedPlanId) return;

    setIsChanging(true);
    billingApi
      .changePlan(selectedPlanId, selectedInterval)
      .then((response) => {
        if (response.credits_carried_over !== undefined) {
          const carried = response.credits_carried_over || 0;
          notify(
            "success",
            `Switched to ${response.new_plan}.${carried > 0 ? ` ${carried} unused credits moved to your extra balance.` : ""}`
          );
        } else if (response.downgrade) {
          const dateStr = response.effective_at
            ? new Date(response.effective_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
            : "";
          notify(
            "success",
            `Your plan will switch to ${response.new_plan}${dateStr ? ` on ${dateStr}` : " at the end of the billing period"}.`
          );
        } else {
          notify("success", `Upgraded to ${response.new_plan} successfully.`);
        }
        setShowChangeModal(false);
        setChangePreview(null);
        refreshPlansAndUser();
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsChanging(false));
  };

  const currentPlanPrice = currentPlan ? +currentPlan.price : 0;
  const currentInterval = upcomingInvoice?.interval || "monthly";

  const isPastDue = currentUser?.billing_status === "past_due";

  const handleUpdatePayment = (): void => {
    billingApi
      .getCustomerPortal()
      .then((response) => {
        window.location.href = response.url;
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      });
  };

  return (
    <div className="plans">
      {isPastDue && (
        <div className="plans__past-due-banner">
          <HiOutlineCreditCard size={18} />
          <span>Your last payment failed. <button className="plans__past-due-link" onClick={handleUpdatePayment}>Update payment method</button> to keep your subscription.</span>
        </div>
      )}
      <div className="plans__alert">
        <ByokKeyAlert />
      </div>
      <h1 className="plans__title">Pricing Plans</h1>
      <p className="plans__subtitle">Choose the best plan to fit your needs.</p>

      <div className="plans__interval-toggle">
        <button
          className={`plans__interval-btn${billingInterval === "monthly" ? " plans__interval-btn--active" : ""}`}
          onClick={() => setBillingInterval("monthly")}
        >
          Monthly
        </button>
        <button
          className={`plans__interval-btn${billingInterval === "yearly" ? " plans__interval-btn--active" : ""}`}
          onClick={() => setBillingInterval("yearly")}
        >
          Yearly
          {(() => {
            // Real percentage from plan prices instead of a hardcoded claim:
            // plans differ (Flex saves ~6%, legacy Pro/Max ~17%), so show the
            // best available saving across the plans this user can see.
            const savings = plans
              .filter((p: IPlan) => p.has_yearly && p.yearly_price && +p.price > 0)
              .map((p: IPlan) => Math.round((1 - +p.yearly_price! / (+p.price * 12)) * 100))
              .filter((pct: number) => pct > 0);
            if (!savings.length) return null;
            const best = Math.max(...savings);
            const label = savings.length > 1 && new Set(savings).size > 1 ? `Save up to ${best}%` : `Save ${best}%`;
            return <span className="plans__save-badge">{label}</span>;
          })()}
        </button>
      </div>

      {/* Initial load: Redux has no plans yet - render a hero-shaped skeleton
          so the page doesn't flash an empty grid and then jump layouts. */}
      {isProcessing && plans.length === 0 ? (
        <div className="plans__hero">
          <PlanSkeleton isLoading={true}><></></PlanSkeleton>
          <div className="plans__hero-features plans__hero-features--skeleton">
            {Array.from({ length: 9 }).map((_, i) => (
              <div className="plans__hero-skeleton-line" key={i} />
            ))}
          </div>
        </div>
      ) : (
      <>
      {/* Legacy (Lite/Pro/Max) subscribers: their plan is no longer on sale,
          so it sits in a strip above the Flex hero instead of a second card. */}
      {userLegacyPlan && (() => {
        const onYearly = currentInterval === "yearly" && userLegacyPlan.has_yearly && userLegacyPlan.yearly_price;
        const legacyPrice = onYearly ? +userLegacyPlan.yearly_price! : +userLegacyPlan.price;
        const cancelDate = cancelAtDate
          ? new Date(cancelAtDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })
          : null;
        return (
          <div className="plans__legacy">
            <div className="plans__legacy-info">
              <span className="plans__legacy-label">Your current plan</span>
              <div className="plans__legacy-plan">
                <strong>{userLegacyPlan.name}</strong>
                <span className="plans__legacy-sep" aria-hidden="true" />
                <span className="plans__legacy-price">
                  ${legacyPrice % 1 ? legacyPrice.toFixed(2) : legacyPrice}/{onYearly ? "year" : "month"}
                </span>
                {userLegacyPlan.ai_credits_per_seat ? (
                  <span className="plans__legacy-sep" aria-hidden="true" />
                ) : null}
                {userLegacyPlan.ai_credits_per_seat ? (
                  <span className="plans__legacy-credits">{userLegacyPlan.ai_credits_per_seat} AI credits / seat</span>
                ) : null}
              </div>
              <span className="plans__legacy-note">
                {isCancelingSubscription
                  ? `Cancels${cancelDate ? ` on ${cancelDate}` : " at the end of the billing period"}.`
                  : "This plan is no longer offered. You can keep it, or move to Flex anytime - unused credits carry over."}
              </span>
            </div>
            <div className="plans__legacy-actions">
              {hasActiveSubscription && !isCancelingSubscription && (
                <button type="button" className="plans__legacy-cancel" onClick={() => setShowCancelModal(true)}>
                  Cancel plan
                </button>
              )}
            </div>
          </div>
        );
      })()}
      <div className="plans__hero">
        {flexPlans.filter((plan) => billingInterval === "monthly" || plan.has_yearly).map((plan: IPlan) => {
          // A plan card represents the user's "current plan" only when the
          // displayed billing interval matches the user's actual subscription
          // interval. Otherwise a yearly subscriber would see "Canceling" on
          // the monthly Pro card (wrong) and nothing on the yearly Pro card.
          // Wait for upcomingInvoice to load before deciding — without this
          // gate, the monthly card briefly flashes "Current Plan" until the
          // upcomingInvoice fetch resolves and reveals `interval = "yearly"`.
          const upcomingReady = !hasActiveSubscription || upcomingInvoice !== null;
          const displayedAsYearly = billingInterval === "yearly" && plan.has_yearly;
          const userOnYearly = currentInterval === "yearly";
          const isCurrentMatch = upcomingReady && plan.is_selected && displayedAsYearly === userOnYearly;
          return (
            <Plan
              key={plan.id}
              id={plan.id}
              title={plan.name as PricingPlan}
              periodName={plan.period_name}
              price={+plan.price}
              priceWithDiscount={plan?.price_discounted}
              yearlyPrice={plan.yearly_price}
              hasYearly={plan.has_yearly}
              billingInterval={billingInterval}
              features={plan.description}
              isCurrent={isCurrentMatch}
              isLoading={isProcessing}
              // Show the spinner state on the action button until upcomingInvoice
              // resolves — otherwise it briefly renders the wrong CTA
              // ("Downgrade" → "Switch") during the gap between getPlans and
              // getUpcomingInvoice for users with an active subscription.
              isProcessing={planIdInProgressToBeCurrent === plan.id || (hasActiveSubscription && !upcomingReady)}
              isRecommended={!!userLegacyPlan}
              onSelect={handleSelect}
              onCancel={isCurrentMatch && hasActiveSubscription && !isCancelingSubscription ? () => setShowCancelModal(true) : undefined}
              hasActiveSubscription={!!hasActiveSubscription}
              currentPlanPrice={currentPlanPrice}
              currentInterval={currentInterval}
              isPending={pendingPlanName === plan.name}
              isCanceling={isCurrentMatch && isCancelingSubscription}
              cancelAt={cancelAtDate}
              isFlexMigration={isFlexMigrationTarget(plan)}
            />
          );
        })}
        <div className="plans__hero-features">
            <div className="plans__hero-features-title">Everything in Upwex, included</div>
            <div className="plans__hero-features-grid">
              {PLAN_FEATURE_GROUPS.map((group) => (
                <div className="plans__hero-group" key={group.title}>
                  <div className="plans__hero-group-title">
                    <group.icon size={15} />
                    {group.title}
                  </div>
                  <ul>
                    {group.items.map((item) => (
                      <li key={item}>
                        <HiOutlineCheck size={14} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
      </div>
      </>
      )}
      <BackButton to={APP_ROUTES.billing}>Back to Billing</BackButton>

      {/* Cancel Subscription Modal */}
      <Modal
        open={showCancelModal}
        className="cancel-subscription-modal"
        closeIcon={<HiOutlineXMark size={20} />}
        onCancel={() => !isCanceling && setShowCancelModal(false)}
        footer={null}
        title={null}
        width={400}
      >
        <div className="cancel-modal">
          <div className="cancel-modal__icon">
            <HiOutlineExclamationTriangle size={28} />
          </div>
          <h3 className="cancel-modal__title">Cancel Subscription</h3>
          <p className="cancel-modal__desc">
            Your <strong>{currentPlan?.name}</strong> plan will remain active until the end of the current billing period. After that, your team will lose access to paid features.
          </p>
          <div className="cancel-modal__buttons">
            <FormButton
              theme="grey"
              onClick={() => setShowCancelModal(false)}
              disabled={isCanceling}
            >
              Keep Plan
            </FormButton>
            <FormButton
              theme="red"
              loading={isCanceling}
              disabled={isCanceling}
              onClick={handleCancelSubscription}
            >
              Cancel Plan
            </FormButton>
          </div>
        </div>
      </Modal>

      {/* Change Plan Confirmation Modal */}
      <Modal
        open={showChangeModal}
        className="change-plan-modal"
        closeIcon={<HiOutlineXMark size={20} />}
        onCancel={() => !isChanging && !isLoadingPreview && setShowChangeModal(false)}
        footer={null}
        title={null}
        width={400}
      >
        {(() => {
          const previewPlan = plans.find((p) => p.id === selectedPlanId);
          // Backend is the source of truth once the preview is in; until then
          // guess from the plans (legacy -> Flex is always an upgrade).
          const isFlexMigration = changePreview?.is_flex_migration ?? (previewPlan ? isFlexMigrationTarget(previewPlan) : false);
          const carryOver = changePreview?.credits_carry_over || 0;
          const previewIsDowngrade = isFlexMigration
            ? false
            : changePreview?.is_downgrade ?? (previewPlan ? +previewPlan.price < currentPlanPrice : false);
          const previewIsUpgrade = isFlexMigration || (changePreview ? changePreview.amount_due > 0 : (previewPlan ? +previewPlan.price >= currentPlanPrice : true));
          const shimmer = <span className="change-modal__shimmer change-modal__shimmer--inline" />;

          return (
            <div className="change-modal">
              <div className={`change-modal__icon ${previewIsDowngrade ? "change-modal__icon--downgrade" : previewIsUpgrade ? "change-modal__icon--upgrade" : "change-modal__icon--switch"}`}>
                {previewIsDowngrade ? <HiOutlineArrowTrendingDown size={28} /> : previewIsUpgrade ? <HiOutlineArrowTrendingUp size={28} /> : <HiOutlineArrowsRightLeft size={28} />}
              </div>
              <h3 className="change-modal__title">
                {previewIsDowngrade ? "Downgrade" : previewIsUpgrade ? "Upgrade" : "Switch"} to {isLoadingPreview ? shimmer : changePreview?.new_plan}
              </h3>
              {(() => {
                const seats = changePreview?.seats || 0;
                const perSeat = changePreview?.new_price || 0;
                const newTotal = perSeat * seats;
                const intLabel = changePreview?.interval === "yearly" ? "/yr" : "/mo";

                return (
                  <>
                    <div className="change-modal__details">
                      <div className="change-modal__row">
                        <span>New plan</span>
                        <strong>{isLoadingPreview ? shimmer : changePreview?.new_plan}</strong>
                      </div>
                      <div className="change-modal__row">
                        <span>Billing</span>
                        <strong>{isLoadingPreview ? shimmer : (changePreview?.interval === "yearly" ? "Yearly" : "Monthly")}</strong>
                      </div>
                      <div className="change-modal__row">
                        <span>Seats</span>
                        <strong>{isLoadingPreview ? shimmer : `${seats} ${seats === 1 ? "member" : "members"}`}</strong>
                      </div>
                      <div className="change-modal__row">
                        <span>Per seat</span>
                        <strong>{isLoadingPreview ? shimmer : `$${perSeat}${intLabel}`}</strong>
                      </div>
                      <div className="change-modal__row change-modal__row--total">
                        <span>New total</span>
                        <strong>{isLoadingPreview ? shimmer : `$${newTotal.toFixed(2)}${intLabel}`}</strong>
                      </div>
                    </div>
                    {previewIsDowngrade ? (
                      <div className="change-modal__downgrade-info">
                        <p>Your current plan stays active until the end of the billing period. The new plan will take effect on your next invoice.</p>
                        <p>No charges or credits. You keep full access to your current plan until renewal.</p>
                      </div>
                    ) : (
                      <div className="change-modal__proration">
                        {isLoadingPreview ? (
                          <>
                            <div className="change-modal__row">
                              <span>{shimmer}</span>
                              <strong>{shimmer}</strong>
                            </div>
                            <div className="change-modal__row">
                              <span>{shimmer}</span>
                              <strong>{shimmer}</strong>
                            </div>
                            <div className="change-modal__row">
                              <span>{shimmer}</span>
                              <strong>{shimmer}</strong>
                            </div>
                          </>
                        ) : changePreview && previewIsUpgrade ? (
                          <>
                            {changePreview.proration_credit > 0 && (
                              <div className="change-modal__row change-modal__row--credit">
                                <span>Credit{changePreview.current_plan_name ? ` (unused ${changePreview.current_plan_name})` : ""}</span>
                                <strong>-${changePreview.proration_credit.toFixed(2)}</strong>
                              </div>
                            )}
                            <div className="change-modal__row">
                              <span>Subtotal</span>
                              <strong>${changePreview.subtotal.toFixed(2)}</strong>
                            </div>
                            {changePreview.tax > 0 && (
                              <div className="change-modal__row">
                                <span>Tax</span>
                                <strong>${changePreview.tax.toFixed(2)}</strong>
                              </div>
                            )}
                            <div className="change-modal__row change-modal__row--highlight">
                              <span>Total due now</span>
                              <strong>${changePreview.amount_due.toFixed(2)}</strong>
                            </div>
                          </>
                        ) : null}
                      </div>
                    )}
                  </>
                );
              })()}
              {isFlexMigration && !isLoadingPreview && (
                <div className="change-modal__migration">
                  <p>The switch to Flex happens right away.</p>
                  {carryOver > 0 && (
                    <p>
                      Your <strong>{carryOver}</strong> unused credits move to your extra balance - extra credits never expire.
                    </p>
                  )}
                </div>
              )}
              {!previewIsDowngrade && (
                <p className="change-modal__note">
                  {`You'll be credited for the unused time on your current plan.${changePreview && changePreview.tax > 0 ? " Tax is calculated based on your location." : ""}`}
                </p>
              )}
              <div className="change-modal__buttons">
                <FormButton
                  theme="grey"
                  onClick={() => setShowChangeModal(false)}
                  disabled={isChanging || isLoadingPreview}
                >
                  Cancel
                </FormButton>
                <FormButton
                  theme="green-light"
                  loading={isChanging}
                  disabled={isChanging || isLoadingPreview}
                  onClick={handleConfirmChange}
                >
                  {previewIsDowngrade ? "Downgrade" : previewIsUpgrade ? "Upgrade" : "Switch"}
                </FormButton>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Seats Confirmation Modal (first subscription with team > 1) */}
      <Modal
        open={showSeatsModal}
        className="seats-confirm-modal"
        closeIcon={<HiOutlineXMark size={20} />}
        onCancel={() => setShowSeatsModal(false)}
        footer={null}
        title={null}
        width={400}
      >
        {(() => {
          const selectedPlan = plans.find((p) => p.id === selectedPlanId);
          const unitPrice = selectedInterval === "yearly"
            ? +(selectedPlan?.yearly_price || 0)
            : +(selectedPlan?.price || 0);
          const total = unitPrice * totalMembers;
          const intervalLabel = selectedInterval === "yearly" ? "/yr" : "/mo";

          return (
            <div className="change-modal">
              <div className="change-modal__icon change-modal__icon--upgrade">
                <HiOutlineUsers size={28} />
              </div>
              <h3 className="change-modal__title">
                Subscribe to {selectedPlan?.name}
              </h3>
              <div className="change-modal__details">
                <div className="change-modal__row">
                  <span>Plan</span>
                  <strong>{selectedPlan?.name}</strong>
                </div>
                <div className="change-modal__row">
                  <span>Billing</span>
                  <strong>{selectedInterval === "yearly" ? "Yearly" : "Monthly"}</strong>
                </div>
                <div className="change-modal__row">
                  <span>Seats</span>
                  <strong>{totalMembers} {totalMembers === 1 ? "member" : "members"}</strong>
                </div>
                <div className="change-modal__row">
                  <span>Per seat</span>
                  <strong>${unitPrice.toFixed(2)}{intervalLabel}</strong>
                </div>
                <div className="change-modal__row change-modal__row--total">
                  <span>Total</span>
                  <strong>${total.toFixed(2)}{intervalLabel}</strong>
                </div>
              </div>
              <p className="change-modal__note">
                Your team has {totalMembers} members. Each member gets a paid seat.
              </p>
              <div className="change-modal__buttons">
                <FormButton
                  theme="grey"
                  onClick={() => setShowSeatsModal(false)}
                >
                  Cancel
                </FormButton>
                <FormButton
                  theme="green-light"
                  onClick={handleConfirmSeats}
                >
                  Subscribe
                </FormButton>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};
